import { trackInfraLoadAnimationFramesView } from "@faire/web-api--source/events/infra/view/loadAnimationFrames";
import { trackInfraLoadUserEventsView } from "@faire/web-api--source/events/infra/view/loadUserEvents";

import { getGlobalProperty } from "../globals/getGlobalProperty";
import { getWindowOrThrow } from "../globals/getWindow";

import {
  detectQuietMainThread,
  IQuietMainThreadResult,
} from "./detectQuietMainThread";
import { ReadonlyAbortNotifier } from "./userPerceivedLoadTiming/AbortNotifier";
import {
  ActionName,
  LoadTimingStatus,
  LoadTimingType,
} from "./userPerceivedLoadTiming/types";

const DEFAULT_MAX_QUIET_WINDOW_WAIT_TIME = 1000;
const FRAME_LATENCY_THRESHOLD = 20;
const MAX_FRAMES_TO_SEND = 200;

type SupportedInteractions = "click" | "keydown";

type LoadTimeInteractions = {
  type: SupportedInteractions;
  time: number;
};

const filterUserInteractions = (
  userInteractions: LoadTimeInteractions[] | undefined,
  type: SupportedInteractions
) => userInteractions?.filter((i) => i.type === type).map((i) => i.time) ?? [];

export const trackQuietMainThread = (
  pageName: ActionName,
  loadType: LoadTimingType | undefined,
  abortNotifier?: ReadonlyAbortNotifier<LoadTimingStatus>
) => {
  const window = getWindowOrThrow();

  return detectQuietMainThread(
    DEFAULT_MAX_QUIET_WINDOW_WAIT_TIME,
    FRAME_LATENCY_THRESHOLD,
    abortNotifier,
    ["CLEAN_UP"]
  )
    .then((result: IQuietMainThreadResult) => {
      // Send load time animation frames
      if (!result.frameDurations) {
        return;
      }

      trackInfraLoadAnimationFramesView(
        result.frameDurations
          .slice(0, MAX_FRAMES_TO_SEND)
          .map((n) => n.toFixed(1))
          .toString(),
        `${result.startOfTracking}`,
        result.status,
        pageName,
        loadType,
        result.status
      );

      // Send load time user activity and clean up
      const captureLoadTimeInteraction = getGlobalProperty(
        "captureLoadTimeInteraction"
      ) as (event: Event) => void | undefined;
      const loadTimeInteractions = getGlobalProperty("loadTimeInteractions") as
        | LoadTimeInteractions[]
        | undefined;

      if (!captureLoadTimeInteraction) {
        return;
      }

      const clickTimings = filterUserInteractions(
        loadTimeInteractions,
        "click"
      );
      const keypressTimings = filterUserInteractions(
        loadTimeInteractions,
        "keydown"
      );

      trackInfraLoadUserEventsView(
        clickTimings.toString(),
        keypressTimings.toString(),
        pageName,
        loadType,
        result.status
      );
    })
    .finally(() => {
      // Send load time user activity and clean up
      const captureLoadTimeInteraction = getGlobalProperty(
        "captureLoadTimeInteraction"
      ) as (event: Event) => void | undefined;
      const loadTimeUserInteractionTimeout = getGlobalProperty(
        "loadTimeUserInteractionTimeout"
      ) as NodeJS.Timeout | undefined;

      if (captureLoadTimeInteraction) {
        window.removeEventListener("click", captureLoadTimeInteraction);
        window.removeEventListener("keydown", captureLoadTimeInteraction);
      }

      if (loadTimeUserInteractionTimeout) {
        clearTimeout(loadTimeUserInteractionTimeout);
      }
    });
};
